/**
 * Acronyms whose casing PascalCase ids do not preserve — `SourceObdId` has to read "OBD", not
 * "Obd". Matched on whole words only, so "Cancel" does not become "CANcel".
 */
const ACRONYMS = ["FMS", "OBD", "CAN", "GPS", "VIN", "IOX"];

/**
 * The id of a Geotab `Source`, however the API handed it over.
 *
 * A `Diagnostic.source` arrives either as the enum id itself (`"SourceObdId"`) or as an object
 * carrying it (`{ id: "SourceObdId" }`). Reading it one way only gives either an empty id or the
 * text "[object Object]", so every reader goes through here.
 *
 * @returns The id, or an empty string where there is none.
 */
export function sourceIdOf(source: unknown): string {
  if (typeof source === "string") {
    return source;
  }
  if (typeof source === "object" && source !== null && "id" in source) {
    const { id } = source;
    return typeof id === "string" ? id : "";
  }
  return "";
}

/**
 * Turns a Geotab enum id into something readable.
 *
 * Geotab reports these as `SourceGoDeviceId` or `DiagnosticEngineSpeedId`; a grid needs
 * "Go Device". The explicit `…NoneId` value means "not set" and becomes an empty string rather
 * than the word "None".
 *
 * @param id - The Geotab id, e.g. `SourceObdId`.
 * @param prefix - The id family, e.g. `Source` or `Diagnostic`.
 */
export function parseGeotabId(id: string, prefix: string): string {
  if (!id || id === `${prefix}NoneId`) {
    return "";
  }

  let name = id;
  if (name.startsWith(prefix)) {
    name = name.slice(prefix.length);
  }
  if (name.endsWith("Id")) {
    name = name.slice(0, -2);
  }

  name = name.replace(/([a-z])([A-Z])/g, "$1 $2");

  for (const acronym of ACRONYMS) {
    name = name.replace(new RegExp(`\\b${acronym}\\b`, "gi"), acronym);
  }

  return name;
}
